import { useMemo, useState } from "react";
// UI Framework
import PauseIcon from "@mui/icons-material/Pause";
import { toast } from "react-toastify";
import { TableCell } from "@mui/material";
import { Alert } from "antd";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
// Common Components
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import FullScreenLoading from "src/Components/Loading/FullScreenLoading";
import Button from "src/Components/Button";
import Modal from "src/Components/Modal/Modal";
// Types
import TableTemplate, {
  IAdditional,
  IAdditionalHeaderProps,
} from "src/Tables/TableTemplate/tableTemplate";
import { Header } from "src/Interfaces/TableInterfaces";
import { Packet } from "src/Interfaces/commonDtos";
import {
  ITaskTableReport,
  IGetPreviewOfferResp,
  IPreviewOffer,
} from "src/Interfaces/automaticMarketMaking";
// Requests
import {
  deleteTasks,
  getTriggerTable,
  getPreviewOffers,
  pauseTasks,
  resumeTasks,
  resendTasks,
} from "src/Requests/automaticMarketMaking";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import {
  setEditTaskRow,
  setIsEdit,
  setTaskRow,
  openAddOrEditModal,
} from "src/Features/AutomaticMarketMaking/automaticMarketMakingSlice";
// Hooks
import { useAppDispatch } from "src/App/hooks";
import { showTaskOperationResult } from "./taskOperationFeedback";

const MINIMUM_TRIGGER_INTERVAL_CONFIRMATION_REQUIRED = 13;

const canSendPreview = (preview: IGetPreviewOfferResp) =>
  preview.isAccountConnected !== false;

const previewOfferReport = (offers: IPreviewOffer[], disconnected: boolean) =>
  offers.map((offer, index) => {
    const disabled = disconnected || offer.disabledByBuyingPower;
    const classname = disabled ? "!bg-slate-100 !text-slate-400" : "";
    return {
      ...offer,
      id: offer.rowNo ?? index,
      row: offer.rowNo ?? index + 1,
      calculatedPrice: offer.calculatedPrice?.toFixed(0),
      calculatedVolume: offer.calculatedVolume?.toFixed(0),
      finalVolume: disconnected ? "غیرفعال (اکانت متصل نیست)" : offer.disabledByBuyingPower
        ? "غیرفعال (قدرت خرید)" : offer.finalVolume?.toFixed(0),
      requiredBuyingPower: offer.requiredBuyingPower?.toLocaleString("en-US"),
      calculatedPriceClassname: classname,
      calculatedVolumeClassname: classname,
      finalVolumeClassname: classname,
      requiredBuyingPowerClassname: classname,
    };
  });

type ManualTriggerAction<T extends Packet<boolean>> = (
  ids: number[],
  overrideMinimumTriggerInterval?: boolean
) => Promise<T>;

type ConfirmationRequester = (message: string) => Promise<boolean>;

interface IConfirmationState {
  message: string;
  resolve: (confirmed: boolean) => void;
}

const useManualTriggerConfirmation = () => {
  const [confirmationState, setConfirmationState] =
    useState<IConfirmationState | null>(null);

  const requestConfirmation: ConfirmationRequester = (message) =>
    new Promise<boolean>((resolve) => {
      setConfirmationState({ message, resolve });
    });

  const closeConfirmation = (confirmed: boolean) => {
    const resolve = confirmationState?.resolve;
    setConfirmationState(null);
    resolve?.(confirmed);
  };

  const confirmationModal = confirmationState ? (
    <Modal
      onClose={() => closeConfirmation(false)}
      modalBoxClassName="compact-confirm-modal"
      UI={
        <div
          dir="rtl"
          className="w-full flex flex-col gap-6 items-stretch text-right"
        >
          <div className="flex flex-col gap-2">
            <div className="text-base font-bold text-slate-800">
              تأیید اجرای دستی
            </div>
            <div className="text-sm leading-6 text-slate-600 whitespace-pre-line">
              {confirmationState.message}
            </div>
          </div>
          <div className="flex flex-row gap-2 w-full">
            <Button
              text="انصراف"
              variant="outlined"
              onClick={() => closeConfirmation(false)}
            />
            <Button
              text="ادامه عملیات"
              variant="contained"
              onClick={() => closeConfirmation(true)}
            />
          </div>
        </div>
      }
    />
  ) : null;

  return { requestConfirmation, confirmationModal };
};

const runManualTriggerAction = async <T extends Packet<boolean>>(
  action: ManualTriggerAction<T>,
  ids: number[],
  requestConfirmation: ConfirmationRequester,
  setLoading?: (loading: boolean) => void
): Promise<T | null> => {
  setLoading?.(true);
  try {
    let response = await action(ids);
    if (
      !response.data &&
      response.errorCode === MINIMUM_TRIGGER_INTERVAL_CONFIRMATION_REQUIRED
    ) {
      setLoading?.(false);
      if (!(await requestConfirmation(response.msg))) return null;
      setLoading?.(true);
      response = await action(ids, true);
    }
    return response;
  } finally {
    setLoading?.(false);
  }
};

interface IPreviewState {
  isOpen: boolean;
  submitFunction: ((e?: any) => void) | null;
  play: boolean;
}

const TaskTableAdditionalHeader = ({
  onAction,
  selectedRowsIds,
}: IAdditionalHeaderProps) => {
  const { requestConfirmation, confirmationModal } =
    useManualTriggerConfirmation();
  // Bulk operations talk to the broker per task and can take a while, so block the
  // screen (and repeated clicks) until the request settles.
  const [loading, setLoading] = useState(false);

  const bulkPauseHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!selectedRowsIds.length || loading) return;
    runManualTriggerAction(pauseTasks, selectedRowsIds, requestConfirmation, setLoading)
      .then((resp) => {
        if (!resp) return;
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const bulkPlayHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!selectedRowsIds.length || loading) return;
    runManualTriggerAction(resumeTasks, selectedRowsIds, requestConfirmation, setLoading)
      .then((resp) => {
        if (!resp) return;
        showTaskOperationResult(resp);
        if (resp.data) {
          onAction();
        }
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };
  const resendHandler = () => {
    if (!selectedRowsIds.length || loading) return;
    runManualTriggerAction(resendTasks, selectedRowsIds, requestConfirmation, setLoading)
      .then((resp) => {
        if (!resp) return;
        showTaskOperationResult(resp);
        if (resp.data) {
          onAction();
        }
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const deleteTasksHandler = () => {
    if (!selectedRowsIds.length || loading) return;
    setLoading(true);
    deleteTasks(selectedRowsIds)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <>
      <TableCell>
        {confirmationModal}
        {loading ? <FullScreenLoading /> : <></>}
      </TableCell>
      <TableCell>
        <div className="flex flex-row">
          <div
            onClick={selectedRowsIds.length ? bulkPauseHandler : undefined}
            className="hover:bg-slate-200 transition-all rounded p-1"
          >
            <PauseIcon
              className={`text-sky-900 cursor-pointer  ${
                selectedRowsIds.length ? "text-sky-900 " : "text-gray-300"
              } transition-all`}
            />
          </div>
          <div
            onClick={selectedRowsIds.length ? bulkPlayHandler : undefined}
            className="hover:bg-slate-200 transition-all rounded p-1"
          >
            <PlayCircleIcon
              className={`text-sky-900 cursor-pointer ${
                selectedRowsIds.length ? "text-sky-900 " : "text-gray-300"
              } transition-all`}
            />
          </div>
        </div>
      </TableCell>
      <TableCell></TableCell>
      <TableCell>
        <Button
          onClick={resendHandler}
          className="table-btn"
          text="ارسال مجدد"
          variant="outlined"
          disabled={!selectedRowsIds.length}
        />
      </TableCell>
      <TableCell>
        <div
          className="hover:bg-red-100 transition-all rounded p-1"
          onClick={deleteTasksHandler}
        >
          <DeleteIcon
            fontSize="small"
            className={`cursor-pointer ${
              selectedRowsIds.length ? "text-red-900 " : "text-gray-300"
            } transition-all`}
          />
        </div>
      </TableCell>
    </>
  );
};

const TaskTableAdditionalCol: React.FC<{
  row: ITaskTableReport;
  onAction: () => void;
}> = ({ row, onAction }) => {
  const { requestConfirmation, confirmationModal } =
    useManualTriggerConfirmation();
  const [isTriggerOpen, setIsTriggerOpen] = useState(false);
  const [previewState, setPreviewState] = useState<IPreviewState>({
    isOpen: false,
    submitFunction: null,
    play: true,
  });
  const [loading, setLoading] = useState(false);
  const [previewOffers, setPreviewOffers] = useState<IGetPreviewOfferResp>(
    {} as IGetPreviewOfferResp
  );

  const dispatch = useAppDispatch();

  function fetchPreviewOffers(play: boolean) {
    if (loading) return;
    setLoading(true);
    setPreviewState({ isOpen: false, submitFunction: null, play });
    setPreviewOffers({} as IGetPreviewOfferResp);
    getPreviewOffers(row.id, play)
      .then((resp) => {
        if (!resp || !Array.isArray(resp.buy) || !Array.isArray(resp.sell)) {
          toast.error(
            `${row.optionName}: امکان دریافت پیش‌نمایش سفارش‌ها وجود ندارد؛ اطلاعات نماد و قیمت تعادلی را بررسی کنید`
          );
          return;
        }
        if (!resp.buy?.length && !resp.sell?.length && resp.isAccountConnected !== false && !resp.blockingReason) {
          toast.error(`${row.optionName}: تنظیمات ارسال برای این تسک خالی است`);
          return;
        }
        setPreviewOffers(resp);
        setPreviewState({
          isOpen: true,
          submitFunction: play ? playTaskHandler : resendHandler,
          play,
        });
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }

  const pauseTaskHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    runManualTriggerAction(
      pauseTasks,
      [row.id],
      requestConfirmation,
      setLoading
    )
      .then((resp) => {
        if (!resp) return;
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler);
  };

  const playTaskHandler = () => {
    runManualTriggerAction(
      resumeTasks,
      [row.id],
      requestConfirmation,
      setLoading
    )
      .then((resp) => {
        if (!resp) return;
        showTaskOperationResult(resp);
        if (resp.data) {
          onAction();
          setPreviewState({ isOpen: false, submitFunction: null, play: true });
        }
      })
      .catch(packetErrorHandler);
  };

  const editHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    dispatch(setTaskRow(row));
    dispatch(openAddOrEditModal());
    dispatch(setIsEdit(true));
    dispatch(
      setEditTaskRow({
        id: row.id,
        insId: row.optionId,
        accountId: row.userId?.toString(),
        basePriceTolerance: row.basePriceTolerance,
        volatilityTolerance: row.volatility,
        basePriceAdjustmentFactor: row.basePriceAdjustmentFactor,
        ivtype:
          row.ivModeStr === "دستی"
            ? "Manual"
            : row.ivModeStr === "اتوماتیک"
            ? "Auto"
            : "Continues",
        ivManualValue: row.ivManualValue ?? 0,
        orderType: row.orderType,
        continuousIV: row.continuousIV,
        minimumTriggerIntervalSeconds: row.minimumTriggerIntervalSeconds ?? 0,
      })
    );
  };

  const resendHandler = () => {
    runManualTriggerAction(
      resendTasks,
      [row.id],
      requestConfirmation,
      setLoading
    )
      .then((resp) => {
        if (!resp) return;
        showTaskOperationResult(resp);
        if (resp.data) {
          onAction();
          setPreviewState({ isOpen: false, submitFunction: null, play: true });
        }
      })
      .catch(packetErrorHandler);
  };

  const triggerTable = useMemo(
    () => (
      <TableTemplate
        TableUI={({ reports }) => <CommonTableUI reports={reports} />}
        getDataTable={getTriggerTable}
        searchParameters={{ taskId: row.id }}
      />
    ),
    [row.id]
  );

  const deleteTaskHandler = () => {
    setLoading(true);
    deleteTasks([row.id])
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const previewOffersHeaders: Header[] = [
    {
      headerName: "calculatedPrice",
      translateHeaderName: "قیمت",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
    {
      headerName: "calculatedVolume",
      translateHeaderName: "حجم محاسباتی",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
    {
      headerName: "finalVolume",
      translateHeaderName: "حجم نهایی",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
    {
      headerName: "requiredBuyingPower",
      translateHeaderName: "قدرت خرید مورد نیاز",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
  ];

  const previewTopInfoHeaders: Header[] = [
    {
      headerName: "account",
      translateHeaderName: "اکانت",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
    {
      headerName: "optionName",
      translateHeaderName: "نماد",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
    {
      headerName: "portfoAsset",
      translateHeaderName: "دارایی",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
    {
      headerName: "orderType",
      translateHeaderName: "نوع سفارش",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
    {
      headerName: "iv",
      translateHeaderName: "IV ارسالی",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
    {
      headerName: "basePrice",
      translateHeaderName: "قیمت پایه محاسباتی",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
    {
      headerName: "equilibriumPrice",
      translateHeaderName: "قیمت تعادلی",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
    {
      headerName: "totalRequiredBuyingPower",
      translateHeaderName: "مجموع قدرت خرید مورد نیاز",
      positionTypeStr: "FirstPage",
      typeStr: "Ltr",
    },
  ];

  const getPreviewOffersModalUI = () => {
    // A one-sided program (buy-only or sell-only) is valid; only both sides empty is not.
    if (previewOffers.buy?.length || previewOffers.sell?.length || previewOffers.blockingReason || previewOffers.isAccountConnected === false)
      return (
        <div className="flex flex-col justify-start items-start w-full gap-8">
          <div className="w-full text-center text-lg">
            <CommonTableUI
              reports={{
                report: [
                  {
                    id: 0,
                    row: 1,
                    account: previewOffers.account,
                    optionName: row.optionName,
                    portfoAsset: previewOffers.portfoAsset,
                    orderType: previewOffers.orderType,
                    iv: previewOffers.iv?.toFixed(2),
                    basePrice: previewOffers.basePrice?.toLocaleString(
                      "en-US",
                      { maximumFractionDigits: 0 }
                    ),
                    equilibriumPrice:
                      previewOffers.equilibriumPrice?.toLocaleString(
                        "en-US",
                        { maximumFractionDigits: 0 }
                      ),
                    totalRequiredBuyingPower:
                      previewOffers.totalRequiredBuyingPower?.toLocaleString(
                        "en-US"
                      ),
                  },
                ],
                translatedHeaders: previewTopInfoHeaders,
              }}
              tableBoxClassName="big-font-table"
            />
          </div>
          <div className="flex flex-row justify-start items-start gap-5 w-full">
            <CommonTableUI
              reports={{
                report: previewOfferReport(previewOffers.buy, previewOffers.isAccountConnected === false),
                translatedHeaders: previewOffersHeaders,
              }}
              tableBoxClassName="big-font-table"
            />
            <CommonTableUI
              reports={{
                report: previewOfferReport(previewOffers.sell, previewOffers.isAccountConnected === false),
                translatedHeaders: previewOffersHeaders,
              }}
              tableBoxClassName="big-font-table"
            />
          </div>
          {previewOffers.isBuyingPowerLimited && [...previewOffers.buy, ...previewOffers.sell].some(offer => offer.disabledByBuyingPower) && <Alert className="w-full" type="warning" showIcon
            message="به علت کم بودن قدرت خرید، برخی از سفارش‌ها ارسال نمی‌شوند."
            description="فقط سطر اول خرید و فروش مجاز است؛ سطرهای غیرفعال ارسال نمی‌شوند." />}
          {(previewOffers.blockingReason || previewOffers.isAccountConnected === false) && <Alert className="w-full" type="warning" showIcon
            message={previewOffers.blockingReason || "اکانت متصل نیست؛ ابتدا اکانت را متصل کنید."} />}
          <div className="mr-auto">
            <Button
              onClick={() => {
                if (canSendPreview(previewOffers) && previewState.submitFunction) previewState.submitFunction();
              }}
              text="ارسال"
              variant="contained"
              loading={loading}
              disabled={!canSendPreview(previewOffers)}
            />
          </div>
        </div>
      );
    return <></>;
  };

  return (
    <>
      <TableCell>
        {loading ? <FullScreenLoading /> : <></>}
        {previewState.isOpen ? (
          <Modal
            onClose={() =>
              setPreviewState({ isOpen: false, submitFunction: null, play: true })
            }
            UI={getPreviewOffersModalUI()}
            modalBoxClassName="!min-h-[200px]"
          />
        ) : (
          <></>
        )}
        {confirmationModal}
        <div
          className="hover:bg-slate-300 transition-all rounded p-1"
          onClick={editHandler}
        >
          <EditIcon className="text-sky-700 cursor-pointer" />
        </div>
      </TableCell>
      <TableCell>
        {row.isActive ? (
          <div
            className="hover:bg-slate-300 transition-all rounded p-1"
            onClick={pauseTaskHandler}
          >
            <PauseIcon className="text-sky-700 cursor-pointer" />
          </div>
        ) : null}
        {row.isActive === false ? (
          <div
            className="hover:bg-slate-300 transition-all rounded p-1"
            onClick={() => fetchPreviewOffers(true)}
          >
            <PlayCircleIcon className="text-sky-700 cursor-pointer" />
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        {isTriggerOpen ? (
          <Modal UI={triggerTable} onClose={() => setIsTriggerOpen(false)} />
        ) : (
          <></>
        )}
        <Button
          onClick={() => setIsTriggerOpen(true)}
          className="table-btn"
          text="تریگرها"
          variant="contained"
        />
      </TableCell>
      <TableCell>
        <Button
          onClick={() => fetchPreviewOffers(false)}
          className="table-btn"
          text="ارسال مجدد"
          variant="contained"
        />
      </TableCell>
      <TableCell>
        <div
          onClick={deleteTaskHandler}
          className="hover:bg-red-100 transition-all rounded p-1"
        >
          <DeleteIcon
            fontSize="small"
            className="text-red-700 cursor-pointer"
          />
        </div>
      </TableCell>
    </>
  );
};

export const TaskTableAdditional: IAdditional<ITaskTableReport> = {
  getHeader: TaskTableAdditionalHeader,
  getRow: TaskTableAdditionalCol,
};
